import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const LANGUAGES = [
  { code: "en", label: "EN", full: "English" },
  { code: "ru", label: "RU", full: "Русский" },
  { code: "bg", label: "BG", full: "Български" },
  { code: "it", label: "IT", full: "Italiano" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const links = [
    { labelKey: "nav.overview", href: "#overview" },
    { labelKey: "nav.transport", href: "#transport" },
    { labelKey: "nav.contracts", href: "#contracts" },
    { labelKey: "nav.profitability", href: "#profitability" },
    { labelKey: "nav.market", href: "#market" },
    { labelKey: "nav.contact", href: "#contact" },
  ];

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-[#0b0b0b]/95 backdrop-blur-md border-b border-[#c9a84c]/20 shadow-lg" : ""
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-[#c9a84c] rounded-full">
              <i className="ri-drop-fill text-[#0b0b0b] text-lg" />
            </div>
            <div>
              <span className="text-white font-bold text-lg tracking-wide">
                Saudi<span className="text-[#c9a84c]">Aramco</span>
              </span>
              <p className="text-[#c9a84c]/70 text-[10px] tracking-widest uppercase font-medium -mt-0.5">
                Investment Portal
              </p>
            </div>
          </div>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6">
            {links.map((l) => (
              <button
                key={l.href}
                onClick={() => scrollTo(l.href)}
                className="text-white/70 hover:text-[#c9a84c] text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                {t(l.labelKey)}
              </button>
            ))}
          </div>

          {/* Right controls */}
          <div className="flex items-center gap-3">
            {/* Language switcher */}
            <div ref={langRef} className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#c9a84c]/40 text-white/80 hover:text-[#c9a84c] text-xs font-bold px-3 py-2 rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-global-line text-sm" />
                {currentLang.label}
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${langOpen ? "rotate-180" : ""}`} />
              </button>

              {langOpen && (
                <div className="absolute top-full right-0 mt-2 bg-[#111] border border-white/10 rounded-xl overflow-hidden shadow-xl min-w-[120px] z-50">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        i18n.changeLanguage(lang.code);
                        setLangOpen(false);
                      }}
                      className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        i18n.language === lang.code
                          ? "bg-[#c9a84c]/15 text-[#c9a84c]"
                          : "text-white/60 hover:bg-white/5 hover:text-white"
                      }`}
                    >
                      <span className="font-bold text-xs w-5">{lang.label}</span>
                      <span>{lang.full}</span>
                      {i18n.language === lang.code && (
                        <i className="ri-check-line ml-auto text-[#c9a84c]" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => scrollTo("#contracts")}
              className="hidden md:block bg-[#c9a84c] hover:bg-[#b8962e] text-[#0b0b0b] text-sm font-bold px-5 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("nav.investNow")}
            </button>

            <button
              className="md:hidden text-white cursor-pointer"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <i className={`text-2xl ${menuOpen ? "ri-close-line" : "ri-menu-3-line"}`} />
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-[#0b0b0b]/98 border-t border-[#c9a84c]/20 px-6 py-4 flex flex-col gap-4">
            {links.map((l) => (
              <button
                key={l.href}
                onClick={() => scrollTo(l.href)}
                className="text-white/80 hover:text-[#c9a84c] text-sm font-medium text-left transition-colors cursor-pointer"
              >
                {t(l.labelKey)}
              </button>
            ))}
            <button
              onClick={() => scrollTo("#contracts")}
              className="bg-[#c9a84c] text-[#0b0b0b] text-sm font-bold px-5 py-2 rounded-full mt-2 cursor-pointer whitespace-nowrap"
            >
              {t("nav.investNow")}
            </button>
          </div>
        )}
      </nav>
    </>
  );
}
